import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import httpRequest from '../utils/request';
import { useSelector } from 'react-redux';
import { PERM_KEYS, effectivePerms } from '../utils/permissions';
import { useModalEscape } from '../utils/useModalEscape';

const roleOptions = [
  { value: 'owner', label: 'Owner' },
  { value: 'admin', label: 'Admin' },
  { value: 'seller', label: 'Vendedor' },
  { value: 'cashier', label: 'Cajero' },
  { value: 'accounting', label: 'Contabilidad' },
  { value: 'warehouse', label: 'Depósito' },
  { value: 'purchasing', label: 'Compras' },
];

export default function Users() {
  const { user } = useSelector((s) => s.auth);
  const navigate = useNavigate();
  const [list, setList] = useState([]);
  const [search, setSearch] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [createError, setCreateError] = useState('');
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', password: '', role: 'cashier' });
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', email: '', password: '' });
  const nameRef = useRef(null);
  const emailRef = useRef(null);
  const passwordRef = useRef(null);

  // ESC key handler for modal
  useModalEscape(showCreate, () => { setShowCreate(false); setCreateError(''); });

  const isValidEmail = (v) => /.+@.+\..+/.test(String(v || '').toLowerCase());

  const canAdmin = user?.role === 'owner' || user?.role === 'admin';

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const { data } = await httpRequest.get('/users');
      setList(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e?.response?.data?.message || 'No se pudieron cargar los usuarios');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchUsers(); }, []);

  const updateUser = async (id, patch) => {
    const idx = list.findIndex(u => u._id === id);
    if (idx === -1) return;
    const prev = list[idx];
    try {
      const optimistic = [...list];
      optimistic[idx] = { ...prev, ...patch };
      setList(optimistic);
      await httpRequest.put(`/users/${id}`, patch);
    } catch (e) {
      setError(e?.response?.data?.message || 'No se pudo actualizar el usuario');
      // revertir
      setList((cur) => {
        const copy = [...cur];
        copy[idx] = prev;
        return copy;
      });
    }
  };

  const startEdit = (u) => {
    setEditingId(u._id);
    setEditForm({ name: u.name || '', email: u.email || '', password: '' });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditForm({ name: '', email: '', password: '' });
  };

  const saveEdit = async (id) => {
    const patch = {};
    if (typeof editForm.name === 'string') patch.name = editForm.name;
    if (typeof editForm.email === 'string') patch.email = editForm.email;
    const pw = (editForm.password || '').trim();
    if (pw) {
      if (pw.length < 6) { setError('La contraseña debe tener al menos 6 caracteres'); return; }
      patch.password = pw;
    } else {
      setError('Ingresá una nueva contraseña');
      return;
    }
    await updateUser(id, patch);
    cancelEdit();
  };

  const createUser = async (e) => {
    e.preventDefault();
    const pw = (form.password || '').trim();
    if (!form.name || !form.email || !pw) {
      setCreateError('Completá nombre, email y contraseña');
      if (!form.name) nameRef.current && nameRef.current.focus();
      else if (!form.email) emailRef.current && emailRef.current.focus();
      else if (!pw) passwordRef.current && passwordRef.current.focus();
      return;
    }
    if (!isValidEmail(form.email)) {
      setCreateError('Ingresá un email válido');
      emailRef.current && emailRef.current.focus();
      return;
    }
    if (pw.length < 6) {
      setCreateError('La contraseña debe tener al menos 6 caracteres');
      passwordRef.current && passwordRef.current.focus();
      return;
    }
    try {
      setCreating(true);
      const { data } = await httpRequest.post('/users', { ...form, password: pw });
      setList((cur) => [data, ...cur]);
      setForm({ name: '', email: '', password: '', role: 'cashier' });
      setShowCreate(false);
      setCreateError('');
    } catch (e) {
      setCreateError(e?.response?.data?.message || 'No se pudo crear el usuario');
    } finally {
      setCreating(false);
    }
  };

  const removeUser = async (id) => {
    if (!window.confirm('¿Eliminar este usuario?')) return;
    const prev = list;
    try {
      setList((cur) => cur.filter(u => u._id !== id));
      await httpRequest.delete(`/users/${id}`);
    } catch (e) {
      setError(e?.response?.data?.message || 'No se pudo eliminar');
      setList(prev);
    }
  };

  return (
    <div style={{ padding: 20, maxWidth: 1100, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 10 }}>
        <h1 style={{ marginTop: 0 }}>👥 Usuarios del Comercio</h1>
        <button onClick={() => navigate('/dashboard')} style={btnNav}>🏠 Dashboard</button>
      </div>

      {error && (
        <div style={{ background: '#fee2e2', color: '#991b1b', border: '1px solid #fecaca', padding: 10, borderRadius: 8, marginBottom: 12 }}>
          {error}
        </div>
      )}

      {/* Toolbar: búsqueda + botón crear */}
      <div style={{ display: 'flex', gap: 12, alignItems: 'stretch', marginBottom: 12, flexWrap: 'wrap', position: 'relative' }}>
        {/* Fake fields to trick chrome autofill - hidden visually */}
        <div style={{ width: 0, height: 0, overflow: 'hidden', position: 'absolute', top: -1000, left: -1000 }}>
          <input type="text" name="fake_username_safeguard" tabIndex="-1" />
          <input type="password" name="fake_password_safeguard" tabIndex="-1" />
        </div>

        <input
          placeholder="🔍 Buscar por nombre o email"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ ...input, minWidth: 260, flex: '1 1 280px' }}
          autoComplete="off"
          name="search_safe_field_v3"
          id="search_safe_field_v3"
          type="search"
        />

        {canAdmin && (
          <button onClick={() => { setForm({ name: '', email: '', password: '', role: 'cashier' }); setCreateError(''); setShowCreate(true); }} style={btnPrimary}>➕ Nuevo usuario</button>
        )}
      </div>


      <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 12, overflowX: 'auto', overflowY: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead style={{ backgroundColor: '#007bff', color: 'white' }}>
            <tr>
              <th style={th}>Nombre</th>
              <th style={th}>Email</th>
              <th style={th}>Rol</th>
              <th style={th}>Activo</th>
              {canAdmin && <th style={th}>Permisos</th>}
              {canAdmin && <th style={th}></th>}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={5} style={{ padding: 16, textAlign: 'center' }}>Cargando…</td></tr>
            ) : list.filter(u => (u.name || '').toLowerCase().includes(search.toLowerCase()) || (u.email || '').toLowerCase().includes(search.toLowerCase())).length === 0 ? (
              <tr><td colSpan={5} style={{ padding: 16, textAlign: 'center' }}>Sin usuarios</td></tr>
            ) : (
              list.filter(u => (u.name || '').toLowerCase().includes(search.toLowerCase()) || (u.email || '').toLowerCase().includes(search.toLowerCase())).map((u) => {
                const isOwnerRow = u.role === 'owner';
                return (
                  <tr key={u._id} style={{ borderTop: '1px solid #f3f4f6' }}>
                    <td style={td}>{u.name}</td>
                    <td style={td}>{u.email}</td>
                    <td style={td}>
                      {canAdmin && !isOwnerRow ? (
                        <select value={u.role} onChange={(e) => updateUser(u._id, { role: e.target.value })} style={input}>
                          {roleOptions.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                        </select>
                      ) : (
                        u.role
                      )}
                    </td>
                    <td style={td}>
                      {canAdmin && !isOwnerRow ? (
                        <input type="checkbox" checked={u.isActive} onChange={(e) => updateUser(u._id, { isActive: e.target.checked })} />
                      ) : (
                        u.isActive ? 'Sí' : 'No'
                      )}
                    </td>
                    {canAdmin && (
                      <td style={{ ...td }}>
                        {(() => {
                          if (isOwnerRow) {
                            return <span style={{ color: '#6b7280' }}>Acceso total (Owner)</span>;
                          }
                          const ep = effectivePerms(u);
                          const ALL_KEYS = [
                            PERM_KEYS.PRODUCTS_ACCESS,
                            PERM_KEYS.SALES_ACCESS,
                            PERM_KEYS.SALES_HISTORY,
                            PERM_KEYS.CUSTOMERS_ACCESS,
                            PERM_KEYS.SUPPLIERS_ACCESS,
                            PERM_KEYS.PURCHASES_ACCESS,
                            PERM_KEYS.CASH_ACCESS,
                            PERM_KEYS.REPORTS_ACCESS,
                            PERM_KEYS.REPORTS_IVA,
                            PERM_KEYS.FISCAL_ACCESS,
                            PERM_KEYS.USERS_MANAGE,
                            PERM_KEYS.INVOICE_ACCESS,
                            PERM_KEYS.INVOICE_REPRINT,
                            PERM_KEYS.INVOICE_VOID,
                            PERM_KEYS.SETTINGS_BUSINESS,
                            PERM_KEYS.SETTINGS_PRINT,
                            PERM_KEYS.QUOTATIONS_ACCESS,
                            PERM_KEYS.SETTINGS_ACCESS,
                            PERM_KEYS.STOCK_MOVEMENTS,
                            PERM_KEYS.SALES_WITHOUT_CASH,
                          ];
                          const patchAndSend = (key, value) => {
                            const nextPerms = ALL_KEYS.reduce((acc, k) => {
                              acc[k] = !!(k === key ? value : ep[k]);
                              return acc;
                            }, {});
                            updateUser(u._id, { permissions: nextPerms });
                          };
                          const label = (k) => {
                            const map = {
                              [PERM_KEYS.PRODUCTS_ACCESS]: 'Productos',
                              [PERM_KEYS.SALES_ACCESS]: 'Ventas',
                              [PERM_KEYS.SALES_HISTORY]: 'Historial',
                              [PERM_KEYS.CUSTOMERS_ACCESS]: 'Clientes',
                              [PERM_KEYS.SUPPLIERS_ACCESS]: 'Proveedores',
                              [PERM_KEYS.PURCHASES_ACCESS]: 'Compras',
                              [PERM_KEYS.CASH_ACCESS]: 'Caja',
                              [PERM_KEYS.REPORTS_ACCESS]: 'Reportes',
                              [PERM_KEYS.REPORTS_IVA]: 'IVA Ventas',
                              [PERM_KEYS.FISCAL_ACCESS]: 'Fiscal',
                              [PERM_KEYS.USERS_MANAGE]: 'Usuarios',
                              [PERM_KEYS.INVOICE_ACCESS]: 'Facturas',
                              [PERM_KEYS.INVOICE_REPRINT]: 'Reimprimir',
                              [PERM_KEYS.INVOICE_VOID]: 'Anular',
                              [PERM_KEYS.SETTINGS_BUSINESS]: 'Empresa',
                              [PERM_KEYS.SETTINGS_PRINT]: 'Impresión',
                              [PERM_KEYS.QUOTATIONS_ACCESS]: 'Presupuestos',
                              [PERM_KEYS.SETTINGS_ACCESS]: 'Configuración',
                              [PERM_KEYS.STOCK_MOVEMENTS]: 'Movimientos Stock',
                              [PERM_KEYS.SALES_WITHOUT_CASH]: 'Vender sin Caja',
                            };
                            return map[k] || k;
                          };
                          return (
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(180px,1fr))', gap: 8 }}>
                              {ALL_KEYS.map((k) => (
                                <label key={k} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                  <input type="checkbox" checked={!!ep[k]} onChange={(e) => patchAndSend(k, e.target.checked)} /> {label(k)}
                                </label>
                              ))}
                            </div>
                          );
                        })()}
                      </td>
                    )}
                    {canAdmin && (
                      <td style={{ ...td, textAlign: 'right' }}>
                        {editingId === u._id ? (
                          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                            <input type="password" placeholder="Nueva contraseña (opcional)" value={editForm.password} onChange={(e) => setEditForm({ ...editForm, password: e.target.value })} style={{ ...input, width: 220 }} />
                            <button onClick={() => saveEdit(u._id)} style={btnPrimary}>Guardar</button>
                            <button onClick={cancelEdit} style={btnMuted}>Cancelar</button>
                          </div>
                        ) : (
                          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                            <button onClick={() => startEdit(u)} style={btnMuted}>Cambiar contraseña</button>
                            {u.role !== 'owner' && (
                              <button onClick={() => removeUser(u._id)} style={btnDanger}>Eliminar</button>
                            )}
                          </div>
                        )}
                      </td>
                    )}
                  </tr>
                )
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Modal Crear usuario */}
      {
        showCreate && (
          <div style={modalOverlay} onClick={() => { setShowCreate(false); setCreateError(''); }}>
            <div style={modalContent} onClick={(e) => e.stopPropagation()}>
              <h2 style={{ marginTop: 0, marginBottom: 14 }}>➕ Nuevo Usuario</h2>
              {createError && (
                <div style={errorBoxStyle} role="alert">{createError}</div>
              )}
              <form onSubmit={createUser}>
                <div style={{ marginBottom: 12 }}>
                  <label style={labelStyle}>Nombre</label>
                  <input ref={nameRef} value={form.name} onChange={(e) => { setForm({ ...form, name: e.target.value }); setCreateError(''); }} style={inputModal} placeholder="Nombre y apellido" />
                </div>
                <div style={{ marginBottom: 12 }}>
                  <label style={labelStyle}>Email</label>
                  <input ref={emailRef} type="email" value={form.email} onChange={(e) => { setForm({ ...form, email: e.target.value }); setCreateError(''); }} style={inputModal} placeholder="correo@ejemplo.com" />
                </div>
                <div style={{ marginBottom: 12 }}>
                  <label style={labelStyle}>Contraseña</label>
                  <input ref={passwordRef} type="password" value={form.password} onChange={(e) => { setForm({ ...form, password: e.target.value }); setCreateError(''); }} style={inputModal} placeholder="Mínimo 6 caracteres" />
                </div>
                <div style={{ marginBottom: 16 }}>
                  <label style={labelStyle}>Rol</label>
                  <select value={form.role} onChange={(e) => { setForm({ ...form, role: e.target.value }); setCreateError(''); }} style={inputModal}>
                    {roleOptions.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                  </select>
                </div>
                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                  <button type="button" onClick={() => { setShowCreate(false); setCreateError(''); }} style={btnMuted}>Cancelar</button>
                  <button type="submit" disabled={creating || !form.name || !isValidEmail(form.email) || (form.password || '').trim().length < 6} style={btnPrimary}>{creating ? 'Creando…' : 'Crear'}</button>
                </div>
              </form>
            </div>
          </div>
        )
      }
    </div >
  );
}

const th = { textAlign: 'left', padding: 12, fontWeight: 700, fontSize: 14, color: '#374151' };
const td = { padding: 10, fontSize: 14, color: '#111827' };
const input = { padding: '8px 10px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 14 };
const btnPrimary = { padding: '10px 16px', borderRadius: 8, border: 'none', background: '#10b981', color: 'white', fontWeight: 700, cursor: 'pointer' };
const btnDanger = { padding: '8px 12px', borderRadius: 8, border: 'none', background: '#ef4444', color: 'white', fontWeight: 700, cursor: 'pointer' };
const btnMuted = { padding: '8px 12px', borderRadius: 8, border: '1px solid #d1d5db', background: '#f9fafb', color: '#111827', fontWeight: 700, cursor: 'pointer' };
const btnNav = { background: '#007bff', color: 'white', border: 'none', padding: '8px 12px', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold', fontSize: '14px' };

// Estilos de modal (consistentes con otras pantallas)
const modalOverlay = { position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 };
const modalContent = { backgroundColor: 'white', padding: 24, borderRadius: 12, boxShadow: '0 10px 30px rgba(0,0,0,0.25)', width: '95%', maxWidth: 520, maxHeight: '90vh', overflowY: 'auto' };
const labelStyle = { display: 'block', fontSize: 13, fontWeight: 700, color: '#374151', marginBottom: 6 };
const inputModal = { width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 14, boxSizing: 'border-box' };
const errorBoxStyle = { marginBottom: 12, padding: '10px 12px', borderRadius: 8, background: '#fee2e2', color: '#991b1b', border: '1px solid #fecaca', fontSize: 14, fontWeight: 600 };
